// frontend/src/utils/videoUtils.ts

export interface ParsedVideo {
  type: 'youtube' | 'vimeo' | 'gdrive' | 'loom' | 'dailymotion' | 'direct' | 'embed' | 'unknown';
  embedUrl: string | null;
  rawUrl: string;
}

/**
 * Normalizes and converts various video URL formats (YouTube, Vimeo, Drive, direct MP4, etc.)
 * into valid embed URLs or playable video sources.
 */
export function parseVideoUrl(url: string | null | undefined): ParsedVideo {
  if (!url || typeof url !== 'string') {
    return { type: 'unknown', embedUrl: null, rawUrl: '' };
  }

  const trimmed = url.trim();
  if (!trimmed) {
    return { type: 'unknown', embedUrl: null, rawUrl: '' };
  }

  // 1. YouTube
  // Matches: youtube.com/watch?v=ID, youtu.be/ID, youtube.com/embed/ID, youtube.com/shorts/ID, etc.
  const ytMatch = trimmed.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?|shorts)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/i);
  if (ytMatch && ytMatch[1]) {
    const videoId = ytMatch[1];
    
    // Check for start time (e.g., t=1m20s, t=80, start=80)
    let startSeconds = 0;
    const timeMatch = trimmed.match(/[?&](?:t|start)=([0-9hms]+)/i);
    if (timeMatch && timeMatch[1]) {
      const tVal = timeMatch[1];
      if (/^\d+$/.test(tVal)) {
        startSeconds = parseInt(tVal, 10);
      } else {
        const hours = tVal.match(/(\d+)h/i);
        const mins = tVal.match(/(\d+)m/i);
        const secs = tVal.match(/(\d+)s/i);
        if (hours) startSeconds += parseInt(hours[1], 10) * 3600;
        if (mins) startSeconds += parseInt(mins[1], 10) * 60;
        if (secs) startSeconds += parseInt(secs[1], 10);
      }
    }

    const params = new URLSearchParams();
    params.set('rel', '0');
    params.set('enablejsapi', '1');
    if (startSeconds > 0) {
      params.set('start', String(startSeconds));
    }
    if (typeof window !== 'undefined' && window.location?.origin) {
      params.set('origin', window.location.origin);
    }

    const queryString = params.toString() ? `?${params.toString()}` : '';

    return {
      type: 'youtube',
      embedUrl: `https://www.youtube-nocookie.com/embed/${videoId}${queryString}`,
      rawUrl: trimmed,
    };
  }

  // 2. Vimeo
  const vimeoMatch = trimmed.match(/vimeo\.com\/(?:video\/)?([0-9]+)/i);
  if (vimeoMatch && vimeoMatch[1]) {
    return {
      type: 'vimeo',
      embedUrl: `https://player.vimeo.com/video/${vimeoMatch[1]}`,
      rawUrl: trimmed,
    };
  }

  // 3. Google Drive
  const gdriveMatch = trimmed.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/i);
  if (gdriveMatch && gdriveMatch[1]) {
    return {
      type: 'gdrive',
      embedUrl: `https://drive.google.com/file/d/${gdriveMatch[1]}/preview`,
      rawUrl: trimmed,
    };
  }

  // 4. Loom
  const loomMatch = trimmed.match(/loom\.com\/(?:share|embed)\/([a-zA-Z0-9_-]+)/i);
  if (loomMatch && loomMatch[1]) {
    return {
      type: 'loom',
      embedUrl: `https://www.loom.com/embed/${loomMatch[1]}`,
      rawUrl: trimmed,
    };
  }

  // 5. DailyMotion
  const dmMatch = trimmed.match(/(?:dailymotion\.com\/video\/|dai\.ly\/)([a-zA-Z0-9]+)/i);
  if (dmMatch && dmMatch[1]) {
    return {
      type: 'dailymotion',
      embedUrl: `https://www.dailymotion.com/embed/video/${dmMatch[1]}`,
      rawUrl: trimmed,
    };
  }

  // 6. Direct Video File (.mp4, .webm, .ogg, .mov, .m4v)
  if (/\.(mp4|webm|ogg|mov|m4v)(\?.*)?$/i.test(trimmed)) {
    return {
      type: 'direct',
      embedUrl: trimmed,
      rawUrl: trimmed,
    };
  }

  // 7. Generic embed URL or HTTP(S) URL
  if (/^https?:\/\//i.test(trimmed)) {
    // If it's a generic youtube or other home page, return unknown
    const isGenericYt = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/?$/i.test(trimmed);
    if (isGenericYt) {
      return { type: 'unknown', embedUrl: null, rawUrl: trimmed };
    }
    return {
      type: 'embed',
      embedUrl: trimmed,
      rawUrl: trimmed,
    };
  }

  return { type: 'unknown', embedUrl: null, rawUrl: trimmed };
}
